import { useState } from "react";
import { Button, Container, Form, FormGroup, Input, Label } from "reactstrap";

import useAlertContext from "Hooks/useAlertContext";
import useFetch from "Hooks/useFetch";
import usePageTitle from "Hooks/usePageTitle";
import styles from "Pages/Tools/SuiteQLSearch/SuiteQLSearch.module.css";

const defaultQuery = "SELECT * FROM transaction WHERE ROWNUM <= 10";

export default function SuiteQLSearch() {
    const [query, setQuery] = useState(defaultQuery);
    const [offset, setOffset] = useState("");
    const [result, setResult] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const { post } = useFetch();
    const { newAlert } = useAlertContext();

    usePageTitle("SuiteQL Search");

    async function runQuery() {
        if (!query.trim()) {
            newAlert({ message: "SuiteQL query is required." });
            return;
        }
        const trimmedOffset = offset.trim();
        const parsedOffset = trimmedOffset === "" ? undefined : Number(trimmedOffset);
        if (
            parsedOffset !== undefined &&
            (!Number.isInteger(parsedOffset) || parsedOffset < 0 || parsedOffset % 1000 !== 0)
        ) {
            newAlert({ message: "Offset must be a non-negative multiple of 1000." });
            return;
        }

        setIsLoading(true);
        setResult("");

        try {
            const response = await post("/api/tools/suiteql_search", {
                query,
                offset: parsedOffset,
            });
            const text = await response.text();

            if (!response.ok) {
                let message = text || "SuiteQL query failed.";
                try {
                    const problem = JSON.parse(text);
                    message = problem.detail ?? problem.title ?? message;
                } catch {
                    // keep raw text
                }

                newAlert({ message });
                setResult(text);
                return;
            }

            try {
                setResult(JSON.stringify(JSON.parse(text), null, 2));
            } catch {
                setResult(text);
            }
        } catch {
            newAlert({ message: "SuiteQL query failed." });
        } finally {
            setIsLoading(false);
        }
    }

    return (
        <Container fluid className={styles.page}>
            <h1 className={styles.title}>SuiteQL Search</h1>
            <Form
                onSubmit={(event) => {
                    event.preventDefault();
                    runQuery();
                }}
            >
                <div className={styles.queryControls}>
                    <FormGroup className={styles.queryGroup}>
                        <Label for="suiteqlQuery">Query</Label>
                        <Input
                            id="suiteqlQuery"
                            type="textarea"
                            className={styles.queryInput}
                            value={query}
                            onChange={(event) => setQuery(event.target.value)}
                        />
                    </FormGroup>
                    <FormGroup className={styles.offsetGroup}>
                        <Label for="suiteqlOffset">Offset</Label>
                        <Input
                            id="suiteqlOffset"
                            type="number"
                            min="0"
                            step="1000"
                            value={offset}
                            onChange={(event) => setOffset(event.target.value)}
                        />
                    </FormGroup>
                </div>
                <Button color="primary" type="submit" disabled={isLoading}>
                    {isLoading ? "Running..." : "Run Query"}
                </Button>
            </Form>

            <section className={styles.resultSection} aria-live="polite">
                <h2 className={styles.resultTitle}>Response JSON</h2>
                <pre className={styles.resultBox}>{result || "{}"}</pre>
            </section>
        </Container>
    );
}
